import React from 'react';
import { useLingui } from '@lingui/react/macro';

import { Button } from '@rover/kibble/core';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import { SEARCH_BY_NAME_OPEN_PARAM } from '@rover/react-lib/src/pages/search/SearchPage/components/SearchByName/constants';
import { getURL } from '@rover/shared/js/utils/getFullUrl';
import { getReferrer } from '@rover/shared/js/utils/referrer';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import { redirect } from '@rover/utilities/url';

import { MemberProfileSearchCTAClickEvent } from '../SitterProfilePage/analyticsEvents';

type Props = {
  serviceType?: string;
  searchParams: URLSearchParams;
  shouldShowRevampLayout?: boolean;
  size?: ButtonProps['size'];
};

const BackToSearchButton = ({
  serviceType,
  searchParams,
  shouldShowRevampLayout = false,
  size,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const canBuildSearchUrl = (): boolean => {
    const requiredParams = ['service_type', 'location_type', 'location', 'pet_type'];
    return requiredParams.some((param) => searchParams.get(param) !== null);
  };

  const goBackToSearch = (): void => {
    try {
      const referrerUrl = getReferrer();
      if (referrerUrl) {
        const currentURL = getURL();
        const isSameOrigin = referrerUrl.origin === currentURL.origin;
        // Check if it's a search page URL
        if (isSameOrigin && referrerUrl.pathname.includes('/search')) {
          // Drop the overlay param (leaks in via referrer) so it doesn't re-open on return
          referrerUrl.searchParams.delete(SEARCH_BY_NAME_OPEN_PARAM);
          redirect(referrerUrl);
          return;
        }
      }
      const canBuildUrl = canBuildSearchUrl();
      if (canBuildUrl) {
        redirect(`/search?${searchParams.toString()}`);
        return;
      }
    } catch (e) {
      // If URL parsing fails, fall back to default search
    }

    // Default fallback to search page without filters
    redirect(`/search`);
  };

  const onClick = (): void => {
    storeCrossPageAnalyticsEvent(new MemberProfileSearchCTAClickEvent());
    goBackToSearch();
  };

  const sittersMessage = t`See more sitters`;
  const walkersMessage = t`See more walkers`;
  const finalProvidersMessage = serviceType === 'dog-walking' ? walkersMessage : sittersMessage;

  return (
    <Button
      onClick={onClick}
      fullWidth={!shouldShowRevampLayout}
      variant="flat"
      px={shouldShowRevampLayout ? '6x' : undefined}
      size={size}
    >
      {finalProvidersMessage}
    </Button>
  );
};

export default BackToSearchButton;
